import { useState, type ReactNode } from 'react';
import api from '../api';
import { withBotCheck } from '../utils/botCheck';
import { Button } from '../components';
import { getOrCreateVisitorId } from '../visitorId';

/**
 * Reusable inline email-capture form. Used in three different surfaces:
 *  - Landing page footer (anonymous; large, prominent)
 *  - Dashboard banner for users who didn't opt in at sign-up (compact)
 *  - Settings → Profile (just the toggle; this component is wrapped)
 *
 * The component is purely presentational — the consumer passes in the
 * surface tag (`source`) which gets attributed to the subscription row,
 * so we can measure conversion per surface.
 *
 * Renders three states inline (no page transitions):
 *  - idle: email input + button
 *  - submitting: button shows spinner
 *  - subscribed: brass success message replaces the form
 */
export interface NewsletterSignupProps {
  source: 'landing_footer' | 'dashboard_banner' | 'settings';
  /** Optional name to attach to the subscription row. */
  name?: string;
  /** Pre-fill the email field — useful from authenticated surfaces. */
  defaultEmail?: string;
  /** What to show beneath the input. */
  helpText?: string | ReactNode;
  /** Layout variant. `compact` is the dashboard banner; `wide` is landing. */
  variant?: 'compact' | 'wide';
  /** Optional callback fired on successful subscribe. */
  onSubscribed?: (email: string) => void;
}

export function NewsletterSignup({
  source,
  name,
  defaultEmail = '',
  helpText,
  variant = 'wide',
  onSubscribed,
}: NewsletterSignupProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await withBotCheck((botCheckToken) => api.subscribeNewsletter({ botCheckToken, email: trimmed,
        source,
        name,
        // Tag against the visitor's journey so the admin sees the
        // newsletter signup alongside other actions from the same
        // browser (anonymous /try, eventual sign-up, etc.).
        visitorId: getOrCreateVisitorId() ?? undefined,
      }));
      setSubscribed(true);
      onSubscribed?.(trimmed);
      // If the email was already on file, swap the success copy slightly.
      if (result.alreadySubscribed) {
        // Already-subscribed state is silent on purpose — no need to
        // tell the user "you're already on the list" louder than the
        // checkmark; that risks feeling like an error.
      }
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (subscribed) {
    return (
      <div
        style={{
          padding: variant === 'wide' ? '14px 18px' : '10px 14px',
          background: 'rgba(181,133,60,0.10)',
          border: '1px solid var(--brass)',
          borderRadius: 'var(--r-md)',
          color: 'var(--brass-deep)',
          fontSize: variant === 'wide' ? 14 : 13,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <svg
          viewBox="0 0 16 16"
          width={variant === 'wide' ? 16 : 14}
          height={variant === 'wide' ? 16 : 14}
          aria-hidden="true"
        >
          <path
            d="M3 8.5 L6.5 12 L13 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Thanks. You're on the list.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexDirection: variant === 'compact' ? 'row' : 'row',
          flexWrap: variant === 'wide' ? 'wrap' : 'nowrap',
          alignItems: 'flex-start',
        }}
      >
        <input
          type="email"
          className="input"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          autoComplete="email"
          inputMode="email"
          style={{
            flex: 1,
            minWidth: variant === 'wide' ? 220 : 0,
            margin: 0,
          }}
        />
        <Button
          onClick={() => handleSubmit()}
          loading={submitting}
          compact={variant === 'compact'}
          variant={variant === 'compact' ? 'brass' : 'primary'}
        >
          {variant === 'compact' ? 'Subscribe' : 'Subscribe'}
        </Button>
      </div>
      {(error || helpText) && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: error
              ? 'var(--oxblood, #8B2E2E)'
              : 'var(--text-decorative)',
            lineHeight: 1.5,
          }}
        >
          {error ?? helpText}
        </div>
      )}
    </form>
  );
}
